<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Añadir un tour virtual a tu sitio WordPress es más sencillo de lo
            que parece. Seas agente inmobiliario, dueño de hotel o empresario,
            integrar un tour interactivo 360° lleva solo unos minutos y no
            requiere conocimientos de programación. Aquí tienes la guía completa
            paso a paso.
          </p>
        </section>

        <section>
          <h2>Método 1 — Integración por iFrame (recomendado)</h2>
          <p>
            Este método funciona en cualquier sitio WordPress sin ningún plugin.
            Cuando publicas un tour en Travvir, obtienes un código de
            integración — un fragmento HTML iFrame estándar. Para añadirlo a tu
            página WordPress:
          </p>
          <ol>
            <li>
              Abre el editor WordPress (Gutenberg) en la página que deseas.
            </li>
            <li>Añade un bloque "HTML personalizado".</li>
            <li>Pega el código iFrame de Travvir en el bloque.</li>
            <li>
              Previsualiza la página para confirmar que el tour se muestra
              correctamente.
            </li>
            <li>Publica.</li>
          </ol>
          <p>
            La integración iFrame es compatible con el editor clásico de
            WordPress, Gutenberg, Elementor, Divi y todos los constructores de
            páginas principales — en cualquier lugar donde puedas añadir un
            bloque o widget HTML personalizado.
          </p>
        </section>

        <section>
          <h2>Método 2 — Plugin de tour virtual para WordPress</h2>
          <p>
            Varios plugins de WordPress admiten la integración de tours
            virtuales 360°. Las opciones populares incluyen WP VR (para alojar
            imágenes equirectangulares de forma local) y Virtual Tour Easy
            (gestor de iFrames). Estos plugins añaden una interfaz amigable para
            gestionar múltiples tours integrados en tu sitio.
          </p>
          <h3>Cuándo usar un plugin</h3>
          <p>
            Usa un plugin si tienes muchos tours en varias páginas y necesitas
            gestión centralizada, si quieres alojar las imágenes del tour
            localmente en tu servidor, o si necesitas gestionar los permisos de
            visualización por rol de usuario.
          </p>
        </section>

        <section>
          <h2>Método 3 — Shortcode</h2>
          <p>
            Si tu tema WordPress admite shortcodes en áreas de contenido, puedes
            crear un wrapper de shortcode: añade el código iFrame mediante
            <code>functions.php</code> o un plugin de snippets. Útil para sitios
            inmobiliarios donde los tours aparecen en plantillas de fichas
            automatizadas.
          </p>
        </section>

        <section>
          <h2>Mejores prácticas para tours virtuales en WordPress</h2>
          <ul>
            <li>
              Define una altura de iFrame de al menos 500 px para escritorio.
            </li>
            <li>Usa CSS responsive para que el tour se adapte a móviles.</li>
            <li>
              Añade un mensaje alternativo dentro de las etiquetas iFrame para
              navegadores que los bloqueen.
            </li>
            <li>
              Coloca el tour encima del pliegue en páginas inmobiliarias — es lo
              primero que verá el visitante.
            </li>
          </ul>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>¿El iFrame funciona con Elementor y Divi?</h3>
          <p>
            Sí — usa el widget o módulo "HTML" en esos constructores y pega el
            código iFrame directamente. Elementor y Divi renderizan los iFrames
            sin modificaciones.
          </p>

          <h3>¿El tour virtual se muestra correctamente en móvil?</h3>
          <p>
            Sí, usando un wrapper responsive. El código de integración de
            Travvir incluye los atributos necesarios para la navegación
            giroscópica en móvil.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/integrar-tour-virtual-sitio-web">Cómo Integrar un Tour Virtual 360° en tu Sitio Web 2026</a></li>
          <li><a href="/blog/es/guia-seo-tour-virtual">Guía SEO para Tours Virtuales: Cómo Posicionarlos en Google 2026</a></li>
          <li><a href="/blog/es/guia-tour-virtual-smartphone">Guía Completa: Crear un Tour Virtual 360° con Smartphone 2026</a></li>
          <li><a href="/blog/es/mejor-software-recorrido-virtual-gratis">Mejor Software de Recorrido Virtual Gratis en 2026: Comparativa Completa</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find((p) => p.slug === "tour-virtual-wordpress");

useBlogSeo({
  title:
    "Cómo Añadir un Tour Virtual en WordPress: Guía Completa 2026 | Travvir",
  description:
    "Integra un tour virtual 360° en tu sitio WordPress en minutos: método iFrame, plugin, shortcode. Guía paso a paso sin código.",
  keywords:
    "tour virtual wordpress, integrar tour virtual wordpress, plugin tour virtual wordpress, iframe wordpress tour 360",
  slug: "tour-virtual-wordpress",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿El iFrame funciona con Elementor y Divi?",
      answer:
        "Sí — usa el widget HTML en esos constructores y pega el código iFrame directamente.",
    },
    {
      question: "¿El tour virtual se muestra correctamente en móvil?",
      answer:
        "Sí con un wrapper responsive. El código Travvir incluye los atributos para el giroscopio.",
    },
  ],
});

</script>
